import * as React from 'react';
import { BPM_LOGO_SVG, PAUSE_SMALL_WHITE_SVG } from 'src/assets';
import { ARROW_LEFT_SVG, ARROW_RIGHT_SVG, PlayerContextConsumer, PlayerContext, PlayerState, PlaybackManager, AudioVideoDefaultProps, DynamicPlaylist, AddToHistory, FullPlaybackMediaInfo, Loader, PLAY_SMALL_WHITE_SVG, ListView, BPMAlbum, BPMCurated, BPMMedia } from 'bpm-web-generic';
import { BrowseDropDown } from '../BrowseDropDown';
import { RouteComponentProps } from 'react-router';
import { shareAlbum } from 'src/manager/ShareManager';
import { curatedSetsAPI } from 'src/api/config';
import { apiAlbumToBPMAlbum } from 'src/transformer';

const NEWS_HEIGHT = 300
const XLARGE_IMG = 440
const LARGE_IMG = 240
const SMALL_IMG = 160
const VIDEO_IMG_H = 180
const VIDEO_IMG_W = VIDEO_IMG_H / 9 * 16
const MARGIN_L = 15
const MARGIN_S = 10

export interface CoverListProps extends AudioVideoDefaultProps, RouteComponentProps {
    context: string;
    headline?: string,
    size: "small" | "large" | "video",
    albums?: BPMAlbum[],
    imageKey?: 'cover' | 'image'
    curatedSets?: BPMCurated[],
    viewAll?: boolean,
    onViewAllClick?: () => void
    style?: React.CSSProperties
    onItemClick?: (item: BPMCurated | BPMAlbum) => void
    loadAlbumsForSet?: (item: BPMCurated) => Promise<BPMAlbum[]>
}


export default class CoverList extends React.Component<CoverListProps, { leftArrowDisabled: boolean, rightArrowDisabled: boolean, loadingId?: string | number }> {

    private internallist: DynamicPlaylist;
    constructor(props: any) {
        super(props);
        this.state = {
            leftArrowDisabled: true,
            rightArrowDisabled: true
        }
        this.internallist = new DynamicPlaylist([], {
            history_mode: AddToHistory.ALL
        }, this.props.context, false)
        if (this.props.albums) {
            this.internallist.replace(this.props.albums.map(album => {
                return {
                    album,
                    track: album.media[0],
                    playbackInfo: album.media[0].stream_info,
                    playlist: this.internallist
                } as FullPlaybackMediaInfo
            }))
        }
    }



    componentDidMount() {
        this.setArrows(0)
        this.windowResize = this.windowResize.bind(this)
        window.addEventListener('resize', this.windowResize)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.windowResize)
    }

    windowResize() {
        this.setArrows(this.scrollView ? this.scrollView.scrollLeft : 0)
    }

    private container: HTMLDivElement | null = null;
    private scrollView: HTMLDivElement | null = null;
    private scrollRequestCode: number = 0;

    private getOffset() {
        switch (this.props.size) {
            case "large":
                return LARGE_IMG + MARGIN_L * 2
            case "small":
                return SMALL_IMG + MARGIN_S * 2
            case "video":
                return VIDEO_IMG_W + MARGIN_S * 2
        }
    }

    private isAlbumSelected(album: BPMAlbum, playerContext: PlayerContext) {
        if (playerContext && playerContext.state >= PlayerState.Loading) {
            return album.media.find(m => m.id == playerContext!.track!.track.id) != undefined
        }
        return false;
    }

    private isCuratedSetSelected(set: BPMCurated, playerContext: PlayerContext) {
        if (playerContext && playerContext.state >= PlayerState.Loading) {
            return (playerContext!.track!.curated_id == set.id)
        }
        return false;
    }

    private onAlbumSelected(album: BPMAlbum, queue: boolean = false) {
        if (queue) {
            PlaybackManager.getInstance().getContentManager().appendList(new DynamicPlaylist([{
                album,
                track: album.media[0],
                playbackInfo: album.media[0].stream_info
            }], {
                history_mode: AddToHistory.ALL
            }, Math.random().toString(), false))
            if (PlaybackManager.getInstance().getPlayingState() == PlayerState.None) {
                PlaybackManager.getInstance().play()
                PlaybackManager.getInstance().startTrack(PlaybackManager.getInstance().getContentManager().getTracks()[0])
            }
        } else {
            PlaybackManager.getInstance().getContentManager().replaceLists(this.internallist)
            PlaybackManager.getInstance().startTrack(this.internallist.tracks().find(a => a.album.id == album.id)!)
            this.props.onAlbumSelected && this.props.onAlbumSelected(album)
        }
    }

    private onCuratedSetSelected(currated: BPMCurated, queue: boolean = false) {
        if (!this.props.loadAlbumsForSet || !currated.playable) {
            return
        }
        this.setState({ loadingId: currated.id })
        const albumPromise = this.props.loadAlbumsForSet(currated)
        albumPromise.then((set) => {
            if (queue) {
                PlaybackManager.getInstance().getContentManager().appendList(new DynamicPlaylist(set.map(album => {
                    return {
                        album,
                        track: album.media[0],
                        playbackInfo: album.media[0].stream_info,
                        playlist: this.internallist,
                        curated_id: currated.id!
                    } as FullPlaybackMediaInfo
                }), {
                    history_mode: AddToHistory.ALL
                }, Math.random().toString(), false))
                if (PlaybackManager.getInstance().getPlayingState() == PlayerState.None) {
                    PlaybackManager.getInstance().play()
                    PlaybackManager.getInstance().startTrack(PlaybackManager.getInstance().getContentManager().getTracks()[0])
                }
            } else {
                this.internallist.replace(set.map(album => {
                    return {
                        album,
                        track: album.media[0],
                        playbackInfo: album.media[0].stream_info,
                        playlist: this.internallist,
                        curated_id: currated.id!
                    } as FullPlaybackMediaInfo
                }))
                PlaybackManager.getInstance().getContentManager().replaceLists(this.internallist)
                PlaybackManager.getInstance().startTrack(this.internallist.tracks()[0]!)
            }
            this.setState({ loadingId: undefined })
        }).catch(err => {
            this.setState({ loadingId: undefined })
        })
    }

    render() {
        const { albums, curatedSets, headline, size, viewAll, onViewAllClick, style, imageKey, onItemClick } = this.props
        return (
            <div ref={e => this.container = e} style={style}>
                <div className="coverlist-header">
                    {headline && <span className="headline">{headline}</span>}
                    {viewAll && <span className="viewall" onClick={onViewAllClick}>{'See All'}</span>}
                    {!viewAll && <span className="viewall"></span>}
                    <div className="arrow-container">
                        <div className="arrow" style={{ cursor: this.state.leftArrowDisabled ? "auto" : "pointer" }} onClick={() => {
                            this.scrollView && this.scrollElements(this.scrollView, -this.getOffset(), 500)
                        }}>
                            <img src={this.state.leftArrowDisabled ? ARROW_LEFT_SVG : ARROW_LEFT_SVG} />
                        </div>
                        <div className="arrow" style={{ cursor: this.state.rightArrowDisabled ? "auto" : "pointer" }} onClick={() => { this.scrollView && this.scrollElements(this.scrollView, this.getOffset(), 500) }}>
                            <img src={this.state.rightArrowDisabled ? ARROW_RIGHT_SVG : ARROW_RIGHT_SVG} />
                        </div>
                    </div>
                </div>
                <div ref={(element) => this.scrollView = element} onScroll={() => this.scrollView && this.setArrows(this.scrollView.scrollLeft)} style={{ display: "flex", flex: 1, overflowX: "scroll" }} className="no-scrollbar">
                    {albums ?
                        albums.map((item, index) => {
                            return (
                                <PlayerContextConsumer key={item.id}>{(value) => {
                                    return (
                                        <CoverListItem  {...this.props}
                                            isPlaying={this.isAlbumSelected(item, value)}
                                            cover={imageKey ? item[imageKey] : item.cover}
                                            headline={item.name}
                                            onItemClick={onItemClick ? () => {
                                                onItemClick(item)
                                            } : undefined}
                                            subhead={ListView.renderArtistLink(item, (type, artist) => {
                                                this.props.history.push(`/artist/${type}/${encodeURIComponent(artist)}`)
                                            })}
                                            exclusive={item.exclusive}
                                            media={item.media}
                                            first={index == 0}
                                            last={index == albums.length - 1}
                                            size={size}
                                            loading={this.state.loadingId == item.id}
                                            info={size == "large" ? `${item.bpm}` : undefined}
                                            onPlay={() => {
                                                this.onAlbumSelected(item)
                                            }} onAddToQueue={() => {
                                                this.onAlbumSelected(item, true)
                                            }} onDownload={(media, target) => {
                                                this.props.onMediaDownlod && this.props.onMediaDownlod(media, false)
                                            }} onAddToCrate={(media) => {
                                                if (this.props.onAddToCrate) {
                                                    if (media) {
                                                        this.props.onAddToCrate!(media)
                                                    } else {
                                                        this.setState({ loadingId: item.id })
                                                        Promise.all(item.media.map(i => {
                                                            return this.props.onAddToCrate!(i)
                                                        })).finally(() => {
                                                            this.setState({ loadingId: undefined })
                                                        })
                                                    }
                                                }
                                            }} onShare={() => {
                                                shareAlbum(item)
                                            }}
                                        />
                                    )
                                }}</PlayerContextConsumer>
                            )
                        })
                        :
                        curatedSets ?
                            curatedSets.map((item, index) => {
                                return (
                                    <PlayerContextConsumer key={item.id}>{(value) => {
                                        return (
                                            <CoverListItem  {...this.props}
                                                isPlaying={this.isCuratedSetSelected(item, value)}
                                                onItemClick={onItemClick ? () => {
                                                    onItemClick(item)
                                                } : undefined}
                                                cover={item.image_url}
                                                headline={item.title}
                                                subhead={item.subtitle.split(',').map((artist, index, array) => {
                                                    return <span key={artist}><span className='artist-link' onClick={(e) => {
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                        this.props.history.push(`/artist/audio/${encodeURIComponent(artist.trim())}`)
                                                    }}>{artist}</span><span>{index == (array.length - 1) ? '' : ', '}</span></span>
                                                })}
                                                first={index == 0}
                                                last={index == curatedSets.length - 1}
                                                size={size}
                                                loading={this.state.loadingId == item.id}
                                                onPlay={item.playable ? () => {
                                                    this.onCuratedSetSelected(item)
                                                } : undefined} onAddToQueue={item.playable ? () => {
                                                    this.onCuratedSetSelected(item, true)
                                                } : undefined} onAddToCrate={item.playable ? () => {
                                                    if (this.props.onAddToCrate) {
                                                        this.setState({ loadingId: item.id })
                                                        curatedSetsAPI.getCuratedSet(item.id as number).then(({ data }) => {
                                                            return Promise.all((data.albums || []).map(apiAlbumToBPMAlbum).reduce((prev, album) => {
                                                                prev = prev.concat(album.media)
                                                                return prev
                                                            }, new Array<BPMMedia>()).map(i => {
                                                                return this.props.onAddToCrate!(i)
                                                            }))
                                                        }).finally(() => {
                                                            this.setState({ loadingId: undefined })
                                                        })
                                                    }

                                                } : undefined}
                                            />
                                        )
                                    }}</PlayerContextConsumer>
                                )
                            })
                            :
                            <></>
                    }
                </div>
            </div>
        )
    }

    private scrollElements(element: HTMLDivElement, offset: number, duration: number) {
        var start = element.scrollLeft;
        var currentTime = 0;
        var increment = 20;
        offset = this.getNewOffset(start, offset)

        let _requestCode = ++this.scrollRequestCode;
        var animateScroll = () => {
            if (_requestCode !== this.scrollRequestCode) {
                return;
            }

            currentTime += increment;
            var val = this.easeInOutQuad(currentTime, start, offset, duration);
            element.scrollLeft = val;
            this.setArrows(element.scrollLeft)
            if (currentTime < duration) {
                setTimeout(animateScroll, increment);
            }
        };
        animateScroll();
    }

    private easeInOutQuad(currentTime: number, startValue: number, offset: number, duration: number) {		//https://stackoverflow.com/questions/52747018/horizontal-scrolling-using-buttons-in-reactjs
        currentTime /= duration / 2;
        if (currentTime < 1) {
            return offset / 2 * currentTime * currentTime + startValue;
        }
        currentTime--;
        return -offset / 2 * (currentTime * (currentTime - 2) - 1) + startValue;
    }

    private getNewOffset(start: number, offset: number): number {
        let rest = start % offset
        return rest < Math.abs(offset) / 2 ? offset - rest : (offset - rest) + Math.abs(offset)
    }

    private setArrows(start: number) {
        let leftDisabled = start == 0
        if (leftDisabled != this.state.leftArrowDisabled) {
            this.setState({
                leftArrowDisabled: leftDisabled
            })
        }
        if (this.container) {
            let end = this.props.albums ? this.props.albums.length : this.props.curatedSets ? this.props.curatedSets.length : 0
            let rightDisabled = end * this.getOffset() - 30 <= start + this.container.offsetWidth
            if (rightDisabled != this.state.rightArrowDisabled) {
                this.setState({
                    rightArrowDisabled: rightDisabled
                })
            }
        }
    }
}

export interface CoverListItemProps {
    isPlaying?: boolean;
    cover?: string,
    headline: string,
    subhead?: string | React.ReactNode,
    first: boolean,
    last: boolean,
    size: "small" | "large" | "video" | "news" | "xlarge",
    info?: string
    onClick?: () => void
    onPlay?: () => void
    onDownload?: (media: BPMMedia, target: HTMLElement) => void
    onAddToCrate?: (media: BPMMedia) => void
    onAddToQueue?: () => void
    onShare?: () => void
    onItemClick?: () => void
    exclusive?: boolean
    loading?: boolean

    style?: React.CSSProperties

    media?: BPMMedia[]

    additionalActions?: Array<{ title: string, onClick: () => void }>
}

export class CoverListItem extends React.Component<CoverListItemProps, {
    isOpen: boolean
}> {

    constructor(props: CoverListItemProps) {
        super(props)
        this.toggle = this.toggle.bind(this);
        this.state = {
            isOpen: false
        }
    }

    toggle() {
        this.setState(prevState => ({
            isOpen: !prevState.isOpen
        }));
    }

    getSizing() {
        switch (this.props.size) {
            case 'xlarge':
                return { margin: `${MARGIN_L}px`, width: XLARGE_IMG, height: XLARGE_IMG }
            case 'large':
                return { margin: `${MARGIN_L}px`, width: LARGE_IMG, height: LARGE_IMG }
            case 'small':
                return { margin: `${MARGIN_S}px`, width: SMALL_IMG, height: SMALL_IMG }
            case 'video':
                return { margin: `${MARGIN_S}px`, width: VIDEO_IMG_W, height: VIDEO_IMG_H }
            case 'news':
                return { margin: `${MARGIN_L}px`, width: NEWS_HEIGHT / 9 * 16, height: NEWS_HEIGHT }
        }
    }

    render() {
        const { cover, headline, subhead, first, last, exclusive, info, onClick,
            style, onItemClick } = this.props
        const { margin, height, width } = this.getSizing()

        let dropDownItems = []
        if (this.props.onDownload && this.props.media) {
            dropDownItems.push({
                title: 'Download', items: this.props.media.map(m => {
                    return {
                        title: m.tag!.name,
                        onClick: (e: React.MouseEvent<HTMLElement>) => {
                            this.props.onDownload!(m, e.currentTarget)
                        }
                    }
                })
            })
        }
        if (this.props.onAddToCrate && this.props.media) {
            dropDownItems.push({
                title: 'Add to Crate', items: this.props.media.map(m => {
                    return {
                        title: m.tag!.name,
                        onClick: (e: React.MouseEvent<HTMLElement>) => {
                            this.props.onAddToCrate!(m)
                        }
                    }
                })
            })
        }
        if (this.props.onAddToQueue) {
            dropDownItems.push({ title: 'Add to Queue', onClick: this.props.onAddToQueue! })
        }
        if (this.props.additionalActions)
            dropDownItems = dropDownItems.concat(this.props.additionalActions)
        if (this.props.onShare) {
            dropDownItems.push({
                title: 'Share link', onClick: this.props.onShare
            })
        }
        return (
            <div className="coverlist-item" style={Object.assign({}, { marginLeft: first ? "0px" : margin, marginRight: last ? "0px" : margin, width: width, cursor: onClick ? 'pointer' : 'auto' }, style || {})} onClick={onClick}>
                <img style={{ height: height, width: width, objectFit: "cover", marginBottom: margin }} src={cover ? cover : BPM_LOGO_SVG} />
                <div style={{ display: "flex", flexDirection: "row" }}>
                    <div style={{ overflow: 'hidden' }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span className={"item-text item-title " + (onItemClick ? 'link' : '')} onClick={onItemClick}>{headline}</span>
                        </div>
                        <span className="item-text item-subtitle">{subhead || ''}</span>
                    </div>

                </div>
                {info && <span className="item-text item-info" style={{ width: width }}>{info}</span>}
                <div style={{ height: 25 }}>
                    {exclusive && <span className='item-info'><span className='exclusive bpm-exclusive-tag'>EXCLUSIVE</span></span>}
                </div>
                {<div className={"play-pause-overlay " + (this.props.loading ? 'loading' : '')}
                    style={{ height: height, width: width, objectFit: "cover", marginBottom: margin, cursor: onItemClick ? 'pointer' : 'auto' }} onClick={onItemClick}>
                    {this.props.loading && <span className='loader'><Loader size={24} /></span>}
                    {this.props.onPlay && <span style={{ cursor: 'pointer' }} onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        if (this.props.isPlaying) {
                            PlaybackManager.getInstance().pause()
                        } else {
                            this.props.onPlay && this.props.onPlay()
                        }
                    }}>
                        {!this.props.loading && <div className="play-background">
                            {this.props.isPlaying && <img src={PAUSE_SMALL_WHITE_SVG} />}
                            {!this.props.isPlaying && <img src={PLAY_SMALL_WHITE_SVG} />}
                        </div>}
                    </span>}
                    <div className='cover-more'>
                        {Boolean(this.props.onAddToCrate || this.props.onAddToQueue || this.props.onDownload) &&
                            <BrowseDropDown style={{ marginLeft: 'auto' }}
                                items={dropDownItems} />}
                    </div>
                </div>}
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./src/components/views/CoverList.tsx